import type { ReactNode } from "react"
import { memo, useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"
import { IconCodeCompareOutline18 as CodeCompare } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { computeSideBySideRows } from "@productlane/lib/src/prosemirror-diff"
import { Button } from "@productlane/ui"
import { LucideIcon } from "@productlane/ui/src/components/lucide-icon"

import type { SuggestionRow } from "../helpCenter/use-article-suggestions"
import type { ArticleLike } from "./docs-change"
import { reviewRouteForRow } from "../helpCenter/review-navigation"
import { useRowMerge } from "./docs-change"
import { changeSection, docsChangeLines } from "./docs-change-lines"

// One article's change as a suggested change: the article and section on top,
// each added line on green with a +, a removed one struck through with a -.
const RowCard = memo(
	({
		row,
		article,
		action,
	}: {
		row: SuggestionRow
		article?: ArticleLike
		// Preview, in the corner of the first card.
		action?: ReactNode
	}) => {
		const { baseHtml, merged, title } = useRowMerge(row, article)
		const { lines, section } = useMemo(() => {
			const sideBySide = computeSideBySideRows(
				baseHtml,
				merged.resultHtml,
				merged.hunkRanges ?? undefined,
			)
			return {
				lines: docsChangeLines(sideBySide),
				section: changeSection(sideBySide),
			}
		}, [baseHtml, merged])
		const icon =
			row.proposals.find((p) => p.proposedIcon)?.proposedIcon ??
			article?.icon ??
			"file"
		return (
			<div
				data-testid="agent-docs-change-card"
				className="overflow-hidden rounded-xl border border-gray-200 bg-white text-sm leading-6 dark:border-gray-700/80 dark:bg-gray-800"
			>
				<div className="flex min-h-10 items-center justify-between gap-3 border-b border-gray-200 px-3.5 py-1.5 dark:border-gray-700/80">
					<span className="flex min-w-0 items-center gap-2 text-xs text-secondary">
						<LucideIcon icon={icon} className="h-3.5 w-3.5 flex-none" />
						<span className="truncate">
							{section ? `${title} · ${section}` : title}
						</span>
					</span>
					{action}
				</div>
				{lines.map((line, index) => (
					<div
						key={`${line.kind}-${index}`}
						data-testid="agent-docs-change-line"
						className={cn(
							"flex gap-3 px-3.5 py-2",
							line.kind === "added"
								? "bg-green-50 dark:bg-green-500/10"
								: "bg-red-50 dark:bg-red-500/10",
						)}
					>
						<span
							aria-hidden
							className={cn(
								"w-2 flex-none font-mono",
								line.kind === "added"
									? "text-green-700 dark:text-green-400"
									: "text-red-700 dark:text-red-400",
							)}
						>
							{line.kind === "added" ? "+" : "-"}
						</span>
						<p
							className={cn(
								"min-w-0",
								line.kind === "added"
									? "text-primary"
									: "text-tertiary line-through",
							)}
						>
							{line.text}
						</p>
					</div>
				))}
			</div>
		)
	},
)

/** Each article's change as a suggested-change card. */
export const DocsAddedText = memo(
	({
		rows,
		articles,
		action,
	}: {
		rows: readonly SuggestionRow[]
		articles: ReadonlyMap<string, ArticleLike>
		action?: ReactNode
	}) => (
		<div data-testid="agent-docs-change-text" className="flex flex-col gap-2">
			{rows.map((row, index) => (
				<RowCard
					key={row.key}
					row={row}
					article={row.articleId ? articles.get(row.articleId) : undefined}
					action={index === 0 ? action : undefined}
				/>
			))}
		</div>
	),
)

// The change reads as the text it adds; Review opens the article in the docs
// editor's review view, with the whole diff and room to edit it.
export const DocsChangeText = memo(
	({
		rows,
		articles,
		loaded,
	}: {
		rows: readonly SuggestionRow[]
		articles: ReadonlyMap<string, ArticleLike>
		loaded: boolean
	}) => {
		const navigate = useNavigate()
		const [first] = rows
		if (!loaded && rows.length === 0) {
			return (
				<div className="h-16 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
			)
		}
		if (!first) return null
		return (
			<DocsAddedText
				rows={rows}
				articles={articles}
				action={
					<Button
						size="sm"
						variant="outline"
						Icon={CodeCompare}
						data-testid="agent-docs-review"
						onClick={() => void navigate({ href: reviewRouteForRow(first) })}
					>
						Preview
					</Button>
				}
			/>
		)
	},
)
